import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FormInput, FormSelect } from '../finance';
import { X, CircleNotch, CheckCircle, Lightbulb } from '@phosphor-icons/react';
import studentsService from '../../services/studentsService';
import transportService from '../../services/transportService';
import './transport-forms.css';

const FEE_TYPE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'term', label: 'Term' },
  { value: 'annual', label: 'Annual' },
  { value: 'per_trip', label: 'Per Trip' }
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'bank', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' }
];

function FeeRecordForm({ fee, onSave, onClose }) {
  const [formData, setFormData] = useState(fee || {
    student_id: '',
    route_id: '',
    fee_type: 'monthly',
    amount_due: 0,
    amount_paid: 0,
    discount: 0,
    payment_method: '',
    payment_date: '',
    receipt_number: '',
    due_date: '',
    notes: ''
  });
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [studentFeeStatus, setStudentFeeStatus] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [studentsData, routesData] = await Promise.all([
          studentsService.listStudents({ limit: 100 }).catch(() => []),
          transportService.listRoutes().catch(() => [])
        ]);
        // Handle paginated or direct response for students
        const studentsList = studentsData?.items || studentsData?.students || 
                            (Array.isArray(studentsData) ? studentsData : []);
        // Handle paginated or direct response for routes
        const routesList = routesData?.items || routesData?.routes || 
                          (Array.isArray(routesData) ? routesData : []);
        console.log('Fetched students:', studentsList);
        console.log('Fetched routes:', routesList);
        setStudents(studentsList);
        setRoutes(routesList);
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load data');
      }
    };
    loadData();
  }, []);

  // Check student's current month fee status when student is selected
  useEffect(() => {
    if (!formData.student_id || !selectedRouteId) {
      console.log('🔄 Student check: Student or route not selected');
      return;
    }
    
    if (fee) {
      console.log('🔄 Student check: Editing existing fee, skipping');
      return;
    }
    
    console.log('🔄 Checking fee status for student:', formData.student_id, 'on route:', selectedRouteId);
    
    const checkStudentFeeStatus = async () => {
      try {
        const feeData = await transportService.getStudentFees(formData.student_id);
        console.log('📥 API response for student fees:', feeData);
        
        const studentFees = Array.isArray(feeData) ? feeData : feeData.fees || [];
        console.log('📋 Student fees array:', studentFees);
        
        // Find ANY fee for current month based on PAYMENT_DATE (for paid fees) or DUE_DATE (for unpaid fees)
        const today = new Date();
        const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        console.log('📅 Current month:', currentMonth);
        
        // PRIORITY 1: Check for PAID fee in current month (should block creation)
        const paidFeeInCurrentMonth = studentFees.find(f => {
          if (String(f.route_id) !== String(selectedRouteId)) return false;
          if (f.is_paid && f.payment_date) {
            const paymentMonth = f.payment_date.substring(0, 7);
            const isMatch = paymentMonth === currentMonth;
            console.log(`   ✅ [PAID] Fee ${f.id}: payment_date=${f.payment_date}, month=${paymentMonth} vs ${currentMonth} → ${isMatch ? 'MATCH - BLOCKING' : 'different month'}`);
            return isMatch;
          }
          return false;
        });
        
        if (paidFeeInCurrentMonth) {
          console.log(`✓ Found PAID fee in current month:`, paidFeeInCurrentMonth);
          setStudentFeeStatus(paidFeeInCurrentMonth);
          return;
        }
        
        // PRIORITY 2: Check for UNPAID fee in current month (should show balance)
        const unpaidFeeInCurrentMonth = studentFees.find(f => {
          if (String(f.route_id) !== String(selectedRouteId)) return false;
          if (!f.is_paid && f.due_date) {
            const dueMonth = f.due_date.substring(0, 7);
            const isMatch = dueMonth === currentMonth;
            console.log(`   ⏰ [UNPAID] Fee ${f.id}: due_date=${f.due_date}, month=${dueMonth} vs ${currentMonth} → ${isMatch ? 'MATCH - SHOW BALANCE' : 'different month'}`);
            return isMatch;
          }
          return false;
        });
        
        console.log(`✓ Current month fee found:`, unpaidFeeInCurrentMonth || 'No fee (will use route fee)');
        setStudentFeeStatus(unpaidFeeInCurrentMonth || null);
      } catch (error) {
        console.error('❌ Failed to check student fee status:', error);
        setStudentFeeStatus(null);
      }
    };
    
    checkStudentFeeStatus();
  }, [formData.student_id, selectedRouteId, fee]);

  // Auto-populate amount_due based on student fee status and route fee
  useEffect(() => {
    console.log('Auto-populate effect triggered. Checking conditions:');
    console.log('  selectedRouteId:', selectedRouteId);
    console.log('  routes.length:', routes.length);
    console.log('  fee:', fee);
    console.log('  studentFeeStatus:', studentFeeStatus);
    
    if (!selectedRouteId) {
      console.log('❌ No route selected yet');
      return;
    }
    
    if (routes.length === 0) {
      console.log('❌ Routes not loaded yet');
      return;
    }
    
    if (fee) {
      console.log('❌ Editing existing fee, skipping auto-populate');
      return;
    }
    
    const selectedRoute = routes.find(r => r.id === selectedRouteId);
    console.log('✓ Selected route:', selectedRoute);
    
    if (!selectedRoute) {
      console.log('❌ Route not found in routes array');
      return;
    }
    
    if (!selectedRoute.fee_amount) {
      console.log('❌ Selected route has no fee_amount');
      return;
    }
    
    console.log('✓ All conditions met. Proceeding with auto-populate...');
    
    // Check if fee is already paid in current month (using payment_date)
    let isFeePaidInCurrentMonth = false;
    if (studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date) {
      const today = new Date();
      const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
      isFeePaidInCurrentMonth = paymentMonth === currentMonth;
      console.log(`💳 Payment check: payment_date month=${paymentMonth}, current=${currentMonth}, isPaid=${isFeePaidInCurrentMonth}`);
    }
    
    // If fee is already paid in current month, clear the field and don't populate
    if (isFeePaidInCurrentMonth) {
      console.log('⛔ PAID FEE: Current month fee is already PAID. Not auto-populating.');
      setFormData(prev => ({
        ...prev,
        amount_due: 0
      }));
      return;
    }
    
    let amountToPopulate = selectedRoute.fee_amount;
    
    // If student has current month UNPAID fee
    if (studentFeeStatus && !studentFeeStatus.is_paid) {
      const balance = studentFeeStatus.amount_due - studentFeeStatus.amount_paid;
      console.log('✓ Student has unpaid fee. Balance calculation:', { 
        amount_due: studentFeeStatus.amount_due, 
        amount_paid: studentFeeStatus.amount_paid, 
        balance 
      });
      
      if (Math.abs(balance) < 0.01) {
        amountToPopulate = selectedRoute.fee_amount;
        console.log(`✅ SCENARIO A/B: Zero balance → Using route fee: GHS ${amountToPopulate}`);
      } else if (balance > 0) {
        amountToPopulate = balance;
        console.log(`✅ SCENARIO C: Outstanding balance → Using balance: GHS ${amountToPopulate}`);
      }
    } else {
      console.log('✓ No unpaid fee found → Using route fee');
    }
    
    const finalAmount = parseFloat(amountToPopulate.toFixed(2));
    console.log('📝 Setting amount_due to:', finalAmount);
    
    setFormData(prev => ({
      ...prev,
      amount_due: finalAmount
    }));
    
  }, [selectedRouteId, routes, studentFeeStatus, fee]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Track route changes separately
    if (name === 'route_id') {
      console.log('🔀 Route select changed:', value);
      setSelectedRouteId(value);
    }
    
    if (name === 'student_id') {
      console.log('👤 Student select changed:', value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date) {
      const today = new Date();
      const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
      
      if (paymentMonth === currentMonth) {
        toast.error('Cannot add fee: Transport fee for current month is already paid');
        return;
      }
    }
    
    if (!formData.student_id || !formData.route_id || !formData.amount_due) {
      toast.error('Student, route, and amount are required');
      return;
    }

    setLoading(true);
    try {
      await onSave(fee?.id, formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{fee ? 'Edit Fee' : 'Create Fee Record'}</h2>
          <button onClick={onClose} className="btn-close"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <FormSelect
              label="Student"
              name="student_id"
              value={formData.student_id}
              onChange={handleChange}
              required
              options={students.map(student => ({
                value: student.id,
                label: `${student.first_name} ${student.last_name}`
              }))}
            />

            <FormSelect
              label="Route"
              name="route_id"
              value={formData.route_id}
              onChange={handleChange}
              required
              options={routes.map(route => ({
                value: route.id,
                label: route.route_name
              }))}
            />
          </div>

          <div className="form-row">
            <FormSelect
              label="Fee Type"
              name="fee_type"
              value={formData.fee_type}
              onChange={handleChange}
              options={FEE_TYPE_OPTIONS}
            />

            <div>
              <FormInput
                label="Amount Due (GHS)"
                name="amount_due"
                type="number"
                value={formData.amount_due}
                onChange={handleChange}
                required
                step="0.01"
                min="0"
                disabled={(() => {
                  // Disable only if fee is paid in current month
                  if (studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date) {
                    const today = new Date();
                    const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                    const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
                    return paymentMonth === currentMonth;
                  }
                  return false;
                })()}
              />
              <small style={{ color: (() => {
                if (studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date) {
                  const today = new Date();
                  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                  const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
                  if (paymentMonth === currentMonth) return '#d32f2f';
                }
                return '#666';
              })(), marginTop: '4px', display: 'block' }}>
                {studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date ? (() => {
                  const today = new Date();
                  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                  const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
                  if (paymentMonth === currentMonth) {
                    return <><CheckCircle size={14} weight="fill" /> Transport fee for current month is PAID. To add a fee for a different month, select another student or contact administrator.</>;
                  }
                })() : studentFeeStatus ? (
                  <>
                    {Math.abs((studentFeeStatus.amount_due - studentFeeStatus.amount_paid)) < 0.01 ? (
                      <><Lightbulb size={14} weight="fill" /> Scenario A/B: Unpaid fee with 0 balance → Using route fee: GHS {(routes.find(r => r.id === selectedRouteId)?.fee_amount || 0).toFixed(2)}</>
                    ) : (
                      <><Lightbulb size={14} weight="fill" /> Scenario C: Outstanding balance GHS {(studentFeeStatus.amount_due - studentFeeStatus.amount_paid).toFixed(2)} → Field auto-populated above <CheckCircle size={14} weight="fill" /></>
                    )}
                  </>
                ) : (
                  <><Lightbulb size={14} weight="fill" /> No unpaid fee found → Using route's monthly fee</>
                )}
              </small>
            </div>
          </div>

          <div className="form-row">
            <FormInput
              label="Amount Paid (GHS)"
              name="amount_paid"
              type="number"
              value={formData.amount_paid}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
            <FormInput
              label="Discount (GHS)"
              name="discount"
              type="number"
              value={formData.discount}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>

          <div className="form-row">
            <FormInput
              label="Payment Date"
              name="payment_date"
              type="date"
              value={formData.payment_date}
              onChange={handleChange}
            />
            <FormInput
              label="Due Date"
              name="due_date"
              type="date"
              value={formData.due_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <FormSelect
              label="Payment Method"
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              options={PAYMENT_METHOD_OPTIONS}
            />

            <FormInput
              label="Receipt Number"
              name="receipt_number"
              value={formData.receipt_number}
              onChange={handleChange}
            />
          </div>

          <FormInput
            label="Notes"
            name="notes"
            as="textarea"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
          />

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || (() => {
              if (studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date) {
                const today = new Date();
                const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
                return paymentMonth === currentMonth;
              }
              return false;
            })()}>
              {(() => {
                if (studentFeeStatus && studentFeeStatus.is_paid && studentFeeStatus.payment_date) {
                  const today = new Date();
                  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                  const paymentMonth = studentFeeStatus.payment_date.substring(0, 7);
                  if (paymentMonth === currentMonth) return 'Fee Already Paid';
                }
                if (loading) {
                  return (
                    <span className="button-loading">
                      <CircleNotch size={16} weight="fill" className="spinner" />
                      Saving...
                    </span>
                  );
                }
                return fee ? 'Update Fee' : 'Create Fee';
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FeeRecordForm;
